'use client';

import { TrendingUp, TrendingDown } from 'lucide-react';
import { PageHeader } from '../../../components/shared/PageHeader';
import { formatCurrency } from '../../../types';
import { getIncomeStatementData } from '../data/mock-data';

export function IncomeStatementPage() {
  const data = getIncomeStatementData();

  return (
    <div className="space-y-6">
      <PageHeader
        title="손익계산서"
        description="손익계산서를 확인합니다."
      />

      {/* 기간 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <div className="text-sm text-muted-foreground">기간</div>
        <div className="text-xl font-bold mt-1">
          {data.periodStart} ~ {data.periodEnd}
        </div>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">총 매출</div>
          <div className="text-2xl font-bold mt-1 text-green-500">
            {formatCurrency(data.revenue.totalRevenue)}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">매출총이익</div>
          <div className="text-2xl font-bold mt-1">
            {formatCurrency(data.grossProfit)}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="text-sm text-muted-foreground">영업이익</div>
          <div className={`text-2xl font-bold mt-1 ${data.operatingIncome >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {formatCurrency(data.operatingIncome)}
          </div>
        </div>
        <div className="p-4 rounded-lg bg-card border border-border">
          <div className="flex items-center gap-2">
            <div className="text-sm text-muted-foreground">당기순이익</div>
            {data.netIncome >= 0 ? (
              <TrendingUp className="w-4 h-4 text-green-500" />
            ) : (
              <TrendingDown className="w-4 h-4 text-red-500" />
            )}
          </div>
          <div className={`text-2xl font-bold mt-1 ${data.netIncome >= 0 ? 'text-green-500' : 'text-red-500'}`}>
            {formatCurrency(data.netIncome)}
          </div>
        </div>
      </div>

      {/* 상세 내역 */}
      <div className="p-6 rounded-lg bg-card border border-border">
        {/* 매출 */}
        <div className="mb-6">
          <h3 className="font-bold text-green-600 dark:text-green-400 mb-3">I. 매출</h3>
          <div className="space-y-2 ml-4">
            {data.revenue.items.map((item) => (
              <div key={item.accountCode} className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  <span className="font-mono mr-2">{item.accountCode}</span>
                  {item.accountName}
                </span>
                <span className="font-mono">{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium ml-4">
            <span>매출 계</span>
            <span className="font-mono text-green-600 dark:text-green-400">
              {formatCurrency(data.revenue.totalRevenue)}
            </span>
          </div>
        </div>

        {/* 매출원가 */}
        <div className="mb-6">
          <h3 className="font-bold text-red-600 dark:text-red-400 mb-3">II. 매출원가</h3>
          <div className="space-y-2 ml-4">
            {data.costOfGoodsSold.items.map((item) => (
              <div key={item.accountCode} className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  <span className="font-mono mr-2">{item.accountCode}</span>
                  {item.accountName}
                </span>
                <span className="font-mono">{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium ml-4">
            <span>매출원가 계</span>
            <span className="font-mono text-red-600 dark:text-red-400">
              ({formatCurrency(data.costOfGoodsSold.totalCOGS)})
            </span>
          </div>
        </div>

        {/* 매출총이익 */}
        <div className="flex justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 font-bold mb-6">
          <span>III. 매출총이익</span>
          <span className="font-mono">{formatCurrency(data.grossProfit)}</span>
        </div>

        {/* 판매비와 관리비 */}
        <div className="mb-6">
          <h3 className="font-bold text-orange-600 dark:text-orange-400 mb-3">IV. 판매비와 관리비</h3>
          <div className="space-y-2 ml-4">
            {data.operatingExpenses.items.map((item) => (
              <div key={item.accountCode} className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  <span className="font-mono mr-2">{item.accountCode}</span>
                  {item.accountName}
                </span>
                <span className="font-mono">{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium ml-4">
            <span>판매비와 관리비 계</span>
            <span className="font-mono text-orange-600 dark:text-orange-400">
              ({formatCurrency(data.operatingExpenses.totalOperatingExpenses)})
            </span>
          </div>
        </div>

        {/* 영업이익 */}
        <div className={`flex justify-between p-3 rounded-lg mb-6 font-bold ${
          data.operatingIncome >= 0
            ? 'bg-green-50 dark:bg-green-900/20'
            : 'bg-red-50 dark:bg-red-900/20'
        }`}>
          <span>V. 영업이익</span>
          <span className="font-mono">{formatCurrency(data.operatingIncome)}</span>
        </div>

        {/* 영업외비용 */}
        <div className="mb-6">
          <h3 className="font-bold text-gray-600 dark:text-gray-400 mb-3">VI. 영업외비용</h3>
          <div className="space-y-2 ml-4">
            {data.otherExpenses.items.map((item) => (
              <div key={item.accountCode} className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  <span className="font-mono mr-2">{item.accountCode}</span>
                  {item.accountName}
                </span>
                <span className="font-mono">{formatCurrency(item.amount)}</span>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-3 pt-2 border-t border-border font-medium ml-4">
            <span>영업외비용 계</span>
            <span className="font-mono">
              ({formatCurrency(data.otherExpenses.totalOtherExpenses)})
            </span>
          </div>
        </div>

        {/* 당기순이익 */}
        <div className={`flex justify-between p-4 rounded-lg font-bold text-lg ${
          data.netIncome >= 0
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
            : 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300'
        }`}>
          <span className="flex items-center gap-2">
            VII. 당기순이익
            {data.netIncome >= 0 ? (
              <TrendingUp className="w-5 h-5" />
            ) : (
              <TrendingDown className="w-5 h-5" />
            )}
          </span>
          <span className="font-mono">{formatCurrency(data.netIncome)}</span>
        </div>
      </div>

      {/* 이익률 분석 */}
      <div className="p-4 rounded-lg bg-card border border-border">
        <h3 className="font-semibold mb-4">이익률 분석</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <div className="text-sm text-muted-foreground">매출총이익률</div>
            <div className="text-xl font-bold mt-1">
              {((data.grossProfit / data.revenue.totalRevenue) * 100).toFixed(1)}%
            </div>
            <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className="h-full bg-blue-500"
                style={{ width: `${(data.grossProfit / data.revenue.totalRevenue) * 100}%` }}
              />
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">영업이익률</div>
            <div className="text-xl font-bold mt-1">
              {((data.operatingIncome / data.revenue.totalRevenue) * 100).toFixed(1)}%
            </div>
            <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${data.operatingIncome >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                style={{ width: `${Math.abs((data.operatingIncome / data.revenue.totalRevenue) * 100)}%` }}
              />
            </div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">순이익률</div>
            <div className={`text-xl font-bold mt-1 ${data.netIncome >= 0 ? 'text-green-500' : 'text-red-500'}`}>
              {((data.netIncome / data.revenue.totalRevenue) * 100).toFixed(1)}%
            </div>
            <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div
                className={`h-full ${data.netIncome >= 0 ? 'bg-green-500' : 'bg-red-500'}`}
                style={{ width: `${Math.abs((data.netIncome / data.revenue.totalRevenue) * 100)}%` }}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
